import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Search } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/Common/dropdown-menu';
import { cn } from '@/lib/utils';
import type { LegendGroup } from './types';

interface Props {
  label: string;
  groups: LegendGroup[];
  hidden: ReadonlySet<string>;
  onToggle: (type: string) => void;
  onShowAll: () => void;
}

/** Toolbar version of the type legend: a dropdown of checkable types, grouped and filterable. */
export function TypeFilterDropdown({ label, groups, hidden, onToggle, onShowAll }: Props) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('');
  const q = filter.trim().toLowerCase();
  const total = groups.reduce((n, g) => n + g.types.length, 0);
  const shown = groups.reduce((n, g) => n + g.types.filter((x) => !hidden.has(x.type)).length, 0);

  return (
    <DropdownMenu onOpenChange={(open) => !open && setFilter('')}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors hover:bg-accent/60',
            shown < total ? 'border-primary text-primary' : 'border-border',
          )}
        >
          {label}
          <span className="tabular-nums text-muted-foreground">
            {shown < total ? `${shown}/${total}` : total}
          </span>
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72 p-0">
        <div className="border-b border-border p-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              // Keep keystrokes in the input instead of the menu's typeahead.
              onKeyDown={(e) => e.stopPropagation()}
              placeholder={t('knowledgeGraph.typesFilterPlaceholder')}
              className="h-7 w-full rounded-md border border-input bg-background pl-7 pr-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
        </div>
        <div className="max-h-80 overflow-y-auto p-1">
          {groups.map((g) => {
            const types = g.types.filter((x) => !q || x.label.toLowerCase().includes(q));
            if (types.length === 0) return null;
            return (
              <div key={g.title} className="pb-1">
                <p className="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {g.title}
                </p>
                {types.map((x) => (
                  <DropdownMenuItem
                    key={x.type}
                    onSelect={(e) => {
                      e.preventDefault();
                      onToggle(x.type);
                    }}
                    className={cn('gap-2 py-1 text-xs', hidden.has(x.type) && 'opacity-50')}
                  >
                    <Check className={cn('h-3.5 w-3.5 shrink-0', hidden.has(x.type) && 'invisible')} />
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: x.color }} />
                    <span className="flex-1 truncate">{x.label}</span>
                    <span className="tabular-nums text-[10px] text-muted-foreground">{x.count}</span>
                  </DropdownMenuItem>
                ))}
              </div>
            );
          })}
        </div>
        {shown < total && (
          <div className="border-t border-border p-1">
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                onShowAll();
              }}
              className="justify-center text-xs"
            >
              {t('knowledgeGraph.showAllTypes')}
            </DropdownMenuItem>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
